import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { getMemorialById, updateMemorial, togglePublish } from '../api/memorials';
import './EditMemorialPage.css';

/**
 * EDIT MEMORIAL PAGE - WITH CLOUDINARY UPLOAD & TEMPLATE SELECTION
 * Upload fajlova DIREKTNO na Cloudinary cloud storage
 * Template selection za različite memorial dizajne
 */

const EditMemorialPage = () => {
    const navigate = useNavigate();
    const { qrId } = useParams();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    const [memorialData, setMemorialData] = useState({
        firstName: '',
        lastName: '',
        yearOfBirth: '',
        yearOfDeath: '',
        location: '',
        lifeStory: '',
        template: 'GOLD_CLASSIC', // Default template
        mediaUrls: []
    });

    const [isPublished, setIsPublished] = useState(false);

    // Template options - match your backend TemplateType enum
    const TEMPLATE_OPTIONS = [
        { value: 'GOLD_CLASSIC', label: 'Klassisches Gold', description: 'Traditionelles Gold- & Dunkel-Design' },
        { value: 'SILVER_ELEGANCE', label: 'Silberne Eleganz', description: 'Eleganter Silberverlauf' },
        { value: 'PURE_WHITE', label: 'Reinweiß', description: 'Klares, weißes Minimal-Design' },
        { value: 'WARM_BEIGE', label: 'Warmes Beige', description: 'Warme Beige- & Brauntöne' },
        { value: 'DEEP_BLACK', label: 'Tiefschwarz', description: 'Edles, dunkles Design' },
        { value: 'ICE_BLUE', label: 'Eisblau', description: 'Kühle eisblaue Akzente' }
    ];

    // CLOUDINARY SETUP - Stavi svoje podatke ovde ili u .env
    const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dunjxafq4';
    const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'lumina-memorials';

    useEffect(() => {
        const fetchMemorialData = async () => {
            try {
                setLoading(true);
                const data = await getMemorialById(qrId);

                const backendTemplate = data.template || data.templateType;
                console.log('📥 Backend returned - template:', data.template, 'templateType:', data.templateType);

                const normalizedTemplate = normalizeTemplate(backendTemplate);
                console.log('✅ Normalized to:', normalizedTemplate);

                setMemorialData({
                    firstName: data.firstName || '',
                    lastName: data.lastName || '',
                    yearOfBirth: data.yearOfBirth?.toString() || '',
                    yearOfDeath: data.yearOfDeath?.toString() || '',
                    location: data.location || '',
                    lifeStory: data.lifeStory || '',
                    template: normalizedTemplate,
                    mediaUrls: data.mediaUrls || []
                });

                setIsPublished(data.isPublished || false);
            } catch (err) {
                console.error('Error fetching memorial:', err);
                setError('Gedenkdaten konnten nicht geladen werden');
            } finally {
                setLoading(false);
            }
        };

        if (qrId) {
            fetchMemorialData();
        }
    }, [qrId]);

    // Normalize template values - fix any reversed or old formats
    const normalizeTemplate = (template) => {
        if (!template) return 'GOLD_CLASSIC';

        const templateMap = {
            // Fix reversed formats
            'CLASSIC_GOLD': 'GOLD_CLASSIC',
            'ELEGANCE_SILVER': 'SILVER_ELEGANCE',
            'WHITE_PURE': 'PURE_WHITE',
            'BEIGE_WARM': 'WARM_BEIGE',
            'BLACK_DEEP': 'DEEP_BLACK',
            'BLUE_ICE': 'ICE_BLUE',
            // Other variations
            'GOLD': 'GOLD_CLASSIC',
            'CLASSIC': 'GOLD_CLASSIC'
        };

        return templateMap[template] || template;
    };

    // const fetchMemorialData = async () => {
    //     try {
    //         setLoading(true);
    //         const data = await getMemorialById(qrId);
    //
    //         const backendTemplate = data.template || data.templateType;
    //         console.log('📥 Backend returned - template:', data.template, 'templateType:', data.templateType);
    //
    //         const normalizedTemplate = normalizeTemplate(backendTemplate);
    //         console.log('✅ Normalized to:', normalizedTemplate);
    //
    //         setMemorialData({
    //             firstName: data.firstName || '',
    //             lastName: data.lastName || '',
    //             yearOfBirth: data.yearOfBirth?.toString() || '',
    //             yearOfDeath: data.yearOfDeath?.toString() || '',
    //             location: data.location || '',
    //             lifeStory: data.lifeStory || '',
    //             template: normalizedTemplate,
    //             mediaUrls: data.mediaUrls || []
    //         });
    //
    //         setIsPublished(data.isPublished || false);
    //     } catch (err) {
    //         console.error('Error fetching memorial:', err);
    //         setError('Gedenkdaten konnten nicht geladen werden');
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMemorialData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTemplateChange = (e) => {
        const newTemplate = e.target.value;
        console.log('🎨 User selected template:', newTemplate);

        setMemorialData(prev => ({
            ...prev,
            template: newTemplate
        }));
    };

    const handleTogglePublish = async () => {
        try {
            await togglePublish(qrId);
            setIsPublished(!isPublished);
        } catch (err) {
            console.error('Error toggling publish:', err);
            alert('Veröffentlichungsstatus konnte nicht geändert werden');
        }
    };

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);

        if (files.length === 0) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime'];
        const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

        if (invalidFiles.length > 0) {
            alert('Nur Bilder (JPG, PNG, GIF, WebP) und Videos (MP4, MOV) sind erlaubt');
            return;
        }

        const maxSize = 10 * 1024 * 1024; // 10MB
        const oversizedFiles = files.filter(file => file.size > maxSize);

        if (oversizedFiles.length > 0) {
            alert('Dateien dürfen nicht größer als 10 MB sein');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const uploadedUrls = await uploadToCloudinary(files);

            setMemorialData(prev => ({
                ...prev,
                mediaUrls: [...prev.mediaUrls, ...uploadedUrls]
            }));

        } catch (err) {
            console.error('Error uploading files:', err);
            setError('Upload fehlgeschlagen. Bitte versuchen Sie es erneut.');
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    const uploadToCloudinary = async (files) => {
        const uploadPromises = files.map(async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
            formData.append('folder', 'lumina-memorials');

            try {
                const response = await fetch(
                    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
                    {
                        method: 'POST',
                        body: formData
                    }
                );

                if (!response.ok) {
                    throw new Error('Upload to Cloudinary failed');
                }

                const data = await response.json();
                return data.secure_url;

            } catch (error) {
                console.error('Cloudinary upload error:', error);
                throw error;
            }
        });

        return Promise.all(uploadPromises);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSaving(true);

        try {
            if (!memorialData.firstName || !memorialData.lastName || !memorialData.lifeStory) {
                throw new Error('Vorname, Nachname und Lebensgeschichte sind erforderlich');
            }

            const finalTemplate = normalizeTemplate(memorialData.template);

            const updateData = {
                firstName: memorialData.firstName,
                lastName: memorialData.lastName,
                yearOfBirth: memorialData.yearOfBirth ? parseInt(memorialData.yearOfBirth, 10) : null,
                yearOfDeath: memorialData.yearOfDeath ? parseInt(memorialData.yearOfDeath, 10) : null,
                location: memorialData.location || null,
                lifeStory: memorialData.lifeStory,
                template: finalTemplate,
                mediaUrls: memorialData.mediaUrls
            };

            console.log('📤 SENDING TO BACKEND:');
            console.log('   - template:', finalTemplate);
            console.log('   - full data:', updateData);

            await updateMemorial(qrId, updateData);
            navigate('/profile');

        } catch (err) {
            console.error('Error updating memorial:', err);
            setError(err?.response?.data?.message || err.message || 'Gedenkseite konnte nicht aktualisiert werden');
        } finally {
            setSaving(false);
        }
    };


    const handleRemoveMedia = (index) => {
        setMemorialData(prev => ({
            ...prev,
            mediaUrls: prev.mediaUrls.filter((_, i) => i !== index)
        }));
    };

    const triggerFileInput = () => {
        document.getElementById('file-input').click();
    };

    if (loading) {
        return (
            <div className="edit-memorial-page">
                <div className="edit-particles">
                    <div className="particles-glow" />
                    {[...Array(20)].map((_, index) => (
                        <div key={index} className="particle" />
                    ))}
                </div>
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Gedenkdaten werden geladen…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="edit-memorial-page">
            <div className="edit-particles">
                <div className="particles-glow" />
                {[...Array(20)].map((_, index) => (
                    <div key={index} className="particle" />
                ))}
            </div>

            <div className="edit-memorial-container">
                <motion.div
                    className="edit-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="edit-title">Gedenkseite bearbeiten</h1>
                    <p className="edit-subtitle">Aktualisieren Sie Informationen und Medien</p>

                    <div className="publish-status-container">
                        <span className="status-label">Veröffentlichungsstatus:</span>
                        <motion.button
                            type="button"
                            className={`publish-toggle ${isPublished ? 'published' : 'draft'}`}
                            onClick={handleTogglePublish}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            disabled={saving || uploading}
                        >
                            <span className="status-indicator"></span>
                            {isPublished ? 'Veröffentlicht' : 'Entwurf'}
                        </motion.button>
                    </div>
                </motion.div>

                {error && (
                    <motion.div
                        className="error-message"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {error}
                    </motion.div>
                )}

                <motion.form
                    className="edit-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <div className="form-section">
                        <h3 className="section-title">Gedenk-Design</h3>
                        <p className="section-description">
                            Wählen Sie den visuellen Stil für diese Gedenkseite
                        </p>

                        <div className="form-group">
                            <label>Vorlagen-Design</label>
                            <div className="template-select-wrapper">
                                <select
                                    name="template"
                                    value={memorialData.template}
                                    onChange={handleTemplateChange}
                                    disabled={saving}
                                    className="template-select"
                                >
                                    {TEMPLATE_OPTIONS.map((template) => (
                                        <option key={template.value} value={template.value}>
                                            {template.label} — {template.description}
                                        </option>
                                    ))}
                                </select>
                                <span className="select-arrow">▼</span>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="section-title">Persönliche Informationen</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Vorname *</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    placeholder="Vorname eingeben"
                                    value={memorialData.firstName}
                                    onChange={handleInputChange}
                                    required
                                    disabled={saving}
                                />
                            </div>
                            <div className="form-group">
                                <label>Nachname *</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    placeholder="Nachname eingeben"
                                    value={memorialData.lastName}
                                    onChange={handleInputChange}
                                    required
                                    disabled={saving}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Geburtsjahr</label>
                                <input
                                    type="number"
                                    name="yearOfBirth"
                                    placeholder="z. B. 1950"
                                    min="1900"
                                    max={new Date().getFullYear()}
                                    value={memorialData.yearOfBirth}
                                    onChange={handleInputChange}
                                    disabled={saving}
                                />
                            </div>
                            <div className="form-group">
                                <label>Sterbejahr</label>
                                <input
                                    type="number"
                                    name="yearOfDeath"
                                    placeholder="z. B. 2020"
                                    min="1900"
                                    max={new Date().getFullYear()}
                                    value={memorialData.yearOfDeath}
                                    onChange={handleInputChange}
                                    disabled={saving}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Ort</label>
                            <input
                                type="text"
                                name="location"
                                placeholder="Stadt, Land"
                                value={memorialData.location}
                                onChange={handleInputChange}
                                disabled={saving}
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="section-title">Lebensgeschichte</h3>
                        <div className="form-group">
                            <label>Biografie *</label>
                            <textarea
                                name="lifeStory"
                                placeholder="Teilen Sie die Geschichte, Erinnerungen und das Vermächtnis…"
                                value={memorialData.lifeStory}
                                onChange={handleInputChange}
                                rows="10"
                                required
                                disabled={saving}
                            />
                            <small className="char-count">
                                {memorialData.lifeStory.length} Zeichen
                            </small>
                        </div>
                    </div>

                    <div className="form-section media-section">
                        <h3 className="section-title">Fotos & Videos</h3>
                        <p className="section-description">
                            Laden Sie Bilder und Videos hoch, um eine Galerie zu erstellen
                        </p>

                        <input
                            type="file"
                            id="file-input"
                            multiple
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/quicktime"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                        />

                        {memorialData.mediaUrls.length > 0 && (
                            <div className="media-grid">
                                {memorialData.mediaUrls.map((url, index) => (
                                    <motion.div
                                        key={index}
                                        className="media-card"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                    >
                                        <div className="media-preview">
                                            {url.includes('video') || url.match(/\.(mp4|mov)$/i) ? (
                                                <video src={url} controls />
                                            ) : (
                                                <img src={url} alt={`Medium ${index + 1}`} />
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="remove-media"
                                            onClick={() => handleRemoveMedia(index)}
                                            disabled={saving || uploading}
                                        >
                                            ✕
                                        </button>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        <motion.button
                            type="button"
                            className="add-media-btn"
                            onClick={triggerFileInput}
                            disabled={saving || uploading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {uploading ? (
                                <>
                                    <span className="btn-icon spinner-small"></span>
                                    Upload in die Cloud…
                                </>
                            ) : (
                                <>
                                    <span className="btn-icon">☁️</span>
                                    In Cloud-Speicher hochladen
                                </>
                            )}
                        </motion.button>

                        <p className="media-note">
                            Unterstützt: JPG, PNG, GIF, WebP, MP4, MOV • Max. 10 MB pro Datei
                        </p>
                    </div>

                    <div className="form-actions">
                        <motion.button
                            type="submit"
                            className="publish-btn"
                            whileHover={!saving ? { scale: 1.02 } : {}}
                            whileTap={!saving ? { scale: 0.98 } : {}}
                            disabled={saving || uploading}
                        >
                            {saving ? 'Wird gespeichert…' : 'Speichern & schließen'}
                        </motion.button>
                    </div>
                </motion.form>

                <motion.button
                    className="back-to-profile-btn"
                    onClick={() => navigate('/profile')}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={saving || uploading}
                >
                    ← Zurück zum Profil
                </motion.button>
            </div>
        </div>
    );
};

export default EditMemorialPage;
